import React from 'react';
import { useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { Icon, Spacing, Text } from '@reservamos/elements';
import { When } from 'react-if';
import { isDocumentableCategory } from 'utils/freeFareDocumentRules';
import InfantDisclaimerText from './InfantDisclaimerText';

/**
 * PassengersStatements component.
 * Render both the passengers disclaimer and the passengers
 * instructions to upload documents.
 *
 * Only renders if features flags are enabled.
 * @returns {JSX.Element} The PassengersStatements component.
 */
const PassengersStatements = () => {
  const {
    features,
    env: { brand },
  } = useSelector((state) => state.whitelabelConfig);
  const passengers = useSelector((state) => state.form?.passengers?.values?.passengers || []);
  const { t } = useTranslation('passengers');

  // Exactly the same predicate as the field, imported rather than rewritten: the version that
  // lived here compared `!== 'general'`, so an `oversold` passenger -- the dynamic form's default
  // when there is overbooking -- read an instruction to upload a gratuidade document they lack.
  const someWithDocumentableCategory = passengers.some(
    (passenger) => passenger.busCategory && isDocumentableCategory(passenger.busCategory),
  );

  // Mirrors the field's own gate so the instruction and the input appear together.
  const showsDocumentField =
    Boolean(features.DOCUMENT_PER_PASSENGER) &&
    (!features.SHOW_DOCUMENT_EXCLUDING_ADULTS || someWithDocumentableCategory);

  return (
    <When
      condition={
        Boolean(features.PASSENGERS_SHOW_ID) ||
        Boolean(features.SHOW_INFANTS_DISCLAIMER) ||
        Boolean(features.SHOW_FOREIGN_DISCLAIMER) ||
        Boolean(features.DOCUMENT_PER_PASSENGER)
      }
    >
      <Spacing vertical size="M">
        <When condition={Boolean(features.PASSENGERS_SHOW_ID)}>
          <Spacing size="S">
            <i className="id-icon" />
            <Text size="S" elementType="span">
              {t('remember_your_official_identification_1')}{' '}
              <Trans
                i18nKey="remember_your_official_identification_2"
                t={t}
                className="font-semibold inline"
                tOptions={{ context: brand }}
                components={{
                  blog: (
                    <a
                      href="https://blog.reservamos.mx/posts/requisitos-para-viajar-en-autobus/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      placeholder
                    </a>
                  ),
                }}
              />
            </Text>
          </Spacing>
        </When>

        <When condition={Boolean(features.SHOW_INFANTS_DISCLAIMER)}>
          <Spacing size="S">
            <i className="minor-icon" />
            <Text size="S">
              <InfantDisclaimerText />
            </Text>
          </Spacing>
          {features.SHOW_SECOND_INFANT_DISCLAIMER && (
            <Spacing size="S">
              <i className="minor-icon" />
              <Text size="S">
                <Trans
                  i18nKey="infant_disclaimer_2"
                  t={t}
                  values={{ context: brand }}
                  components={{
                    1: <strong />,
                    ul: <ul />,
                    li: <li />,
                    br: <br />,
                  }}
                />
              </Text>
            </Spacing>
          )}
        </When>

        {/*
          These three notices tell the passenger their document will be reviewed within two
          business days and that a refusal arrives by WhatsApp and e-mail — the contract the
          Admin gratuidade review actually fulfils. They used to sit INSIDE the
          SHOW_FOREIGN_DISCLAIMER block, so a brand could carry
          SHOW_DOCUMENT_VALIDATION_DISCLAIMER and still never show them. Foreign passengers and
          document validation are unrelated questions.
        */}
        <When
          condition={Boolean(features.SHOW_DOCUMENT_VALIDATION_DISCLAIMER) && showsDocumentField}
        >
          <Spacing size="S">
            <Icon type="CircleInfo" size="S" />
            <Spacing size="S" vertical>
              <Text size="S">{t('instructions.file_upload_validation_notice_1')}</Text>
              <Text size="S">{t('instructions.file_upload_validation_notice_2')}</Text>
              <Text size="S">{t('instructions.file_upload_validation_notice_3')}</Text>
            </Spacing>
          </Spacing>
        </When>

        <When condition={Boolean(features.SHOW_FOREIGN_DISCLAIMER)}>
          <Spacing size="S">
            <Icon type="CircleInfo" size="S" />
            <Spacing size="S" vertical>
              <Text size="S">{t('foreign_disclaimer_1', { context: brand })}</Text>
              <Text size="S">{t('foreign_disclaimer_2', { context: brand })}</Text>
              <Text size="S">{t('foreign_disclaimer_3', { context: brand })}</Text>
            </Spacing>
          </Spacing>
        </When>

        {/*
          Gated exactly like the field itself (`showDocumentField` in DynamicPassengersForm,
          `isFieldAllowedForCategory` in PersonPassengersForm). Without the category half, a
          `general` passenger of a brand with SHOW_DOCUMENT_EXCLUDING_ADULTS read an instruction
          to upload a file and then found no field to upload it into.
        */}
        <When condition={showsDocumentField}>
          <Spacing vertical size="S">
            <Spacing size="S">
              <Icon type="CircleInfo" size="S" />
              <Spacing size="XS" vertical>
                <Text size="S">{t('instructions.file_upload')}</Text>
              </Spacing>
            </Spacing>
          </Spacing>
        </When>
      </Spacing>
    </When>
  );
};

export default PassengersStatements;
